import {
  ARTICLE_CODE_HEADER,
  QTY_HEADER,
  aggregateShipmentExcelRows,
  normalizeArticleCodeKey,
  parseShipmentExcelQty,
  readShipmentExcelRows,
  resolveShipmentExcelColumns,
} from './shipmentExcelImport'

/**
 * Parse + validate an Excel file for manual webhook assignment.
 * Reuses the shipment Excel format: Article Code | Qty.
 *
 * Qty is the shipped quantity (final_quantity) for that SKU on the shipment.
 * It must be a whole number between 0 and the current assigned quantity.
 * Omitted shipment SKUs are left unchanged by the caller.
 *
 * @param {ArrayBuffer|Uint8Array} arrayBuffer
 * @param {{ lines: Array<{ material: string, name?: string, quantity: number }> }} context
 * @returns {{
 *   ok: boolean,
 *   errors: Array<{ message: string, excelRow?: number, articleCode?: string }>,
 *   warnings: Array<{ message: string, articleCode?: string }>,
 *   updates: Array<{ material: string, finalQuantity: number, name?: string, currentAssigned: number }>,
 * }}
 */
export function parseAndValidateManualWebhookExcel(arrayBuffer, context = {}) {
  const lines = context.lines || []
  const errors = []
  const warnings = []

  let rows
  try {
    ;({ rows } = readShipmentExcelRows(arrayBuffer))
  } catch (err) {
    return {
      ok: false,
      errors: [{ message: err?.message || 'The Excel file could not be parsed.' }],
      warnings: [],
      updates: [],
    }
  }

  if (!rows.length) {
    return {
      ok: false,
      errors: [
        {
          message:
            'The Excel file has no data rows. Add Article Code and Qty columns with at least one row.',
        },
      ],
      warnings: [],
      updates: [],
    }
  }

  const { articleKey, qtyKey } = resolveShipmentExcelColumns(rows[0])
  if (!articleKey || !qtyKey) {
    const missing = []
    if (!articleKey) missing.push(`"${ARTICLE_CODE_HEADER}"`)
    if (!qtyKey) missing.push(`"${QTY_HEADER}"`)
    return {
      ok: false,
      errors: [
        {
          message: `Missing required column(s): ${missing.join(' and ')}. The file must include exactly these columns (order does not matter): ${ARTICLE_CODE_HEADER} | ${QTY_HEADER}.`,
        },
      ],
      warnings: [],
      updates: [],
    }
  }

  const bySku = new Map()
  for (const line of lines) {
    const material = String(line.material || '').trim()
    if (!material) continue
    bySku.set(normalizeArticleCodeKey(material), line)
  }

  const parsedRows = []
  rows.forEach((row, index) => {
    const excelRow = index + 2
    const articleCode = String(row[articleKey] ?? '').trim()
    const qtyResult = parseShipmentExcelQty(row[qtyKey])

    if (!articleCode) {
      errors.push({
        excelRow,
        message: `Row ${excelRow}: Article Code is missing.`,
      })
      return
    }

    if (!qtyResult.ok) {
      errors.push({
        excelRow,
        articleCode,
        message: `Row ${excelRow} (${articleCode}): ${qtyResult.error}.`,
      })
      return
    }

    parsedRows.push({
      articleCode,
      quantity: qtyResult.value,
      excelRow,
    })
  })

  if (errors.length) {
    return { ok: false, errors, warnings, updates: [] }
  }

  const aggregated = aggregateShipmentExcelRows(parsedRows)
  const updates = []

  for (const entry of aggregated) {
    const key = normalizeArticleCodeKey(entry.articleCode)
    const line = bySku.get(key)

    if (!line) {
      errors.push({
        articleCode: entry.articleCode,
        excelRow: entry.excelRows[0],
        message: `Article code "${entry.articleCode}" was not found on this shipment. Only articles on the current shipment can be assigned.`,
      })
      continue
    }

    const currentAssigned = Number(line.quantity)
    if (!Number.isFinite(currentAssigned) || currentAssigned < 0) {
      errors.push({
        articleCode: entry.articleCode,
        excelRow: entry.excelRows[0],
        message: `Article "${entry.articleCode}": current assigned quantity is invalid.`,
      })
      continue
    }

    if (entry.quantity > currentAssigned) {
      errors.push({
        articleCode: entry.articleCode,
        excelRow: entry.excelRows[0],
        message: `Article "${entry.articleCode}": shipped quantity ${entry.quantity} cannot exceed current assigned (${currentAssigned}).`,
      })
      continue
    }

    if (entry.sourceRowCount > 1) {
      warnings.push({
        articleCode: entry.articleCode,
        message: `Article "${entry.articleCode}" appeared ${entry.sourceRowCount} times; quantities were summed to ${entry.quantity}.`,
      })
    }

    updates.push({
      material: line.material,
      name: line.name,
      currentAssigned,
      finalQuantity: entry.quantity,
    })
  }

  if (errors.length) {
    return { ok: false, errors, warnings, updates: [] }
  }

  if (!updates.length) {
    return {
      ok: false,
      errors: [
        {
          message:
            'No valid article rows were found. Add at least one Article Code with a Qty.',
        },
      ],
      warnings,
      updates: [],
    }
  }

  return { ok: true, errors: [], warnings, updates }
}

export { ARTICLE_CODE_HEADER, QTY_HEADER }
